/**
 * The measured values: papers, sheet size, built-in fonts.
 *
 * Taken out of `main.js` and carried over unchanged — these are measurements,
 * not settings. Whoever changes a number here changes how the sheet looks
 * when printed. The English comments stay English: they describe the
 * measurement, not the code around it.
 */

import type { BuiltinFont, Paper, PaperId } from '../types.js'

/**
 * The built-in fonts.
 *
 * Atkinson Hyperlegible, from the Braille Institute: drawn for
 * distinguishability, so that similar letters cannot be confused — I/l/1 and
 * O/0 are clearly different in shape. For someone who still matches letters
 * while reading, that is the difference between deciphering and reading. It
 * stays the default, and it is the way back if a handwriting is hard to read.
 *
 * Elmar is his own handwriting, from the `kritzl-font` project. Built in
 * rather than dragged in, so that it is simply there on every machine — the
 * size of the font follows from its measured x-height like any other, so it
 * sits on the rules without a setting.
 *
 * Both are declared in the CSS with `@font-face` and loaded from /font/,
 * which is why `registerFont()` skips them.
 */
export const BUILTIN_FONTS: BuiltinFont[] = [
  {
    id: 'builtin',
    family: 'Atkinson Hyperlegible',
    name: 'Atkinson Hyperlegible',
    builtin: true,
  },
  {
    id: 'elmar',
    family: 'Elmar',
    name: 'Elmar',
    builtin: true,
  },
]

/** The default when nothing is chosen or a chosen font has vanished. */
export const DEFAULT_FONT: BuiltinFont = BUILTIN_FONTS[0]!

/**
 * The x-height to which all fonts are brought.
 *
 * In school exercise books the x-height fills around 40 % of the line
 * spacing, and that is where this started. It leaves no room for the red
 * squiggles: a wavy underline sits below the descenders, and with one under
 * every marked word the lines read as crammed together — visibly so on ruled
 * paper, where most of the writing happens.
 *
 * 34 % gives the wave its space. The font is 15 % smaller for it, which a
 * child still reads easily at this size.
 *
 * Squared paper keeps its own ratio below: there the letters fill the square
 * and there is a blank row to fall into.
 *
 * Instead of fixing a point size per font, the calculation goes to this
 * measure: every added handwriting then sits correctly on the rules by
 * itself, no matter how large it was drawn.
 */
export const X_HEIGHT_RATIO = 0.34

/**
 * The same for squared paper — there the font fills the square.
 *
 * In a squared exercise book one writes into the squares: the x-height takes
 * almost the whole square height, ascenders and descenders reach into the
 * neighbouring squares. On ruled paper this is different — there space for
 * ascenders and descenders must remain between two rules, so only 40 % there.
 */
export const X_HEIGHT_RATIO_GRID = 0.62

/**
 * The backgrounds, in millimetres, following the German school rulings.
 *
 * The calculation runs in mm, not in pixels: the sheet is true DIN A4, and
 * what is called 9 mm must measure 9 mm when printed. Pixels would be only a
 * screen approximation and wrong on paper.
 *
 * `line`  Line spacing in mm — for ruled paper the distance between rules,
 *         for squared paper the square height. This is NOT the same measure:
 *         ruling 27 has 9 mm lines, the matching squared paper (ruling 28)
 *         has 7 mm squares. In a squared exercise book one writes one line
 *         per row of squares.
 * `size`  Font size in mm — the FALLBACK only. What is really used is
 *         measured: `fontSizeFor` reads the x-height out of the loaded font
 *         and works back from `X_HEIGHT_RATIO`, which is what lets an added
 *         handwriting sit on the rules without a table entry of its own.
 *         These numbers apply when the measurement fails, and they follow the
 *         same rule: size = line x 0.34 / 0.517.
 * `rows`  Lines per sheet, a whole number. The type area follows from it —
 *         the other way round a cut-off line would remain at the bottom.
 * `left`  Left margin in mm; for squared paper brought to whole squares, so
 *         that the grid works out at the start of the text.
 */
export const PAPERS: Record<PaperId, Paper> = {
  ruled27: {
    label: 'Liniert', hint: '9 mm · Lineatur 27', kind: 'ruled',
    line: 9, size: 5.92, rows: 28, left: 25, right: 15, top: 20,
  },
  ruled4: {
    label: 'Liniert eng', hint: '8 mm · Lineatur 4', kind: 'ruled',
    line: 8, size: 5.26, rows: 32, left: 25, right: 15, top: 20,
  },
  grid7: {
    label: 'Kariert', hint: '7 mm · Lineatur 28', kind: 'grid',
    line: 7, size: 4.60, rows: 37, left: 28, right: 14, top: 20, width: 168,
  },
  grid5: {
    label: 'Kariert eng', hint: '5 mm · Mathe', kind: 'grid',
    line: 5, size: 3.29, rows: 51, left: 25, right: 15, top: 20, width: 170,
  },
  blank: {
    label: 'Blanko', hint: 'ohne Linien', kind: 'blank',
    line: 9, size: 5.92, rows: 28, left: 25, right: 15, top: 20,
  },
}

/** DIN A4 in millimetres. */
export const A4 = { w: 210, h: 297 }

export const DEFAULT_PAPER: PaperId = 'ruled27'

/** The paper for an id, with the default paper as a fallback. */
export const paperDef = (id: PaperId): Paper => PAPERS[id] ?? PAPERS[DEFAULT_PAPER]!
